// Schémas Zod du module « Réglages » (coordonnées émetteur).
//
// Un seul jeu de réglages par cliente (upsert). Tous les champs sont optionnels : la
// cliente complète au fur et à mesure. Le formulaire envoie `null` pour un champ vidé :
// les champs texte sont donc `.nullable().optional()` (un `.optional()` seul rejetterait
// `null` -> 400). Rappel leçon du 23/06.

import { z } from 'zod'

// Champ texte optionnel : chaîne bornée, `null` (champ vidé) ou `undefined`.
const texteOptionnel = (max: number, message: string) =>
  z.string().trim().max(max, message).nullable().optional()

// Lien externe optionnel (RACC) : URL http(s) bornée, `null` (champ vidé) ou `undefined`.
// On borne aux schémas http(s) car ces liens sont ouverts dans un onglet (window.open) :
// on refuse javascript:, data:, etc.
const urlOptionnelle = (message: string) =>
  z
    .string()
    .trim()
    .max(500, 'Lien trop long (500 caractères max).')
    .nullable()
    .optional()
    .refine((v) => v == null || v === '' || /^https?:\/\//i.test(v), message)

// Outil d'étiquettes Canva par défaut (RACC) : ouvert quand la cliente n'a pas renseigné
// son propre modèle. Source : page officielle Canva « Label maker ».
export const URL_CANVA_ETIQUETTES_DEFAUT = 'https://www.canva.com/create/labels/'

// ─── Brique C : régime fiscal et social (micro-entrepreneur) ───────────────────────────
// Tous les taux sont des POURCENTAGES éditables par la cliente. Les valeurs par défaut
// correspondent à 2026 pour une activité de VENTE DE MARCHANDISES (BIC). Sources : URSSAF
// (autoentrepreneur.urssaf.fr), service-public.gouv.fr (vérifié 01/01/2026), economie.gouv.fr.
export const MILLESIME_TAUX = '2026'

const pourcentage = (max: number) => z.number().min(0).max(max)

export const parametresFiscauxSchema = z.object({
  // Cotisations sociales URSSAF (vente de marchandises BIC = 12,3 % en 2026).
  tauxCotisation: pourcentage(100).default(12.3),
  // Contribution à la formation professionnelle (artisan 0,3 % / commerçant 0,1 %).
  tauxCfp: pourcentage(10).default(0.3),
  // Versement libératoire de l'IR (option) : si actif, IR = CA × tauxVersementLiberatoire.
  versementLiberatoire: z.boolean().default(false),
  tauxVersementLiberatoire: pourcentage(10).default(1),
  // Abattement forfaitaire IR (vente de marchandises = 71 %). Sert à la base imposable.
  tauxAbattement: pourcentage(100).default(71),
  // ACRE : réduction des cotisations sociales (50 % la 1re année si éligible).
  acreActive: z.boolean().default(false),
  tauxAcre: pourcentage(100).default(50),
  // Périodicité de déclaration du CA à l'URSSAF (pour les rappels du flux agenda, brique E2).
  periodiciteUrssaf: z.enum(['mensuel', 'trimestriel']).default('trimestriel'),
})
export type ParametresFiscaux = z.infer<typeof parametresFiscauxSchema>

// Valeurs par défaut 2026 (vente de marchandises BIC), appliquées tant que la cliente
// n'a rien personnalisé.
export const PARAMETRES_FISCAUX_DEFAUT: ParametresFiscaux = parametresFiscauxSchema.parse({})

// Mention par défaut en franchise de base de TVA (art. 293 B du CGI).
export const MENTION_TVA_293B = 'TVA non applicable, art. 293 B du CGI'

// Régime de TVA de la cliente (réglage par compte). Source unique de vérité (enum SQL regime_tva).
export const REGIMES_TVA = ['franchise', 'reel'] as const
export type RegimeTva = (typeof REGIMES_TVA)[number]

export const LIBELLES_REGIME_TVA: Record<RegimeTva, string> = {
  franchise: 'Franchise en base (art. 293 B du CGI)',
  reel: 'TVA au réel',
}

// Taux de TVA appliqué par défaut à une facture quand le régime est « réel » (taux standard
// français). Modifiable ligne à ligne... non : au niveau de la facture, dans le brouillon.
export const TVA_TAUX_DEFAUT_REEL = 20

// Taille maximale du champ logo (en caractères). Un logo téléversé est stocké comme image
// embarquée (data URL base64) directement dans `logo_url` ; ~300 000 car. ≈ 220 Ko de fichier.
// Couvre aussi une simple URL http(s). Partagé avec le front (src/pages/Reglages.tsx) pour
// éviter toute divergence de limite entre client et serveur.
export const LOGO_MAX_OCTETS = 300_000

// Un logo valide est : vide/null, OU une URL http(s), OU une image embarquée data:image/...
// (png, jpeg, webp, gif). On refuse tout autre schéma (ex. javascript:, data:text/html) — c'est
// affiché tel quel dans un <img src>, donc on borne aux sources d'image légitimes.
const logoValide = (v: string | null | undefined): boolean =>
  v == null ||
  v === '' ||
  /^https?:\/\//i.test(v) ||
  /^data:image\/(png|jpe?g|webp|gif);base64,/i.test(v)

export const reglagesUpsertSchema = z.object({
  raisonSociale: texteOptionnel(200, 'Raison sociale trop longue (200 caractères max).'),
  adresse: texteOptionnel(300, 'Adresse trop longue (300 caractères max).'),
  codePostal: texteOptionnel(20, 'Code postal trop long (20 caractères max).'),
  ville: texteOptionnel(120, 'Ville trop longue (120 caractères max).'),
  pays: z.string().trim().max(80, 'Pays trop long (80 caractères max).').default('France'),
  siret: texteOptionnel(20, 'SIRET trop long (20 caractères max).'),
  numeroTva: texteOptionnel(40, 'N° de TVA trop long (40 caractères max).'),
  email: texteOptionnel(160, 'E-mail trop long (160 caractères max).'),
  telephone: texteOptionnel(40, 'Téléphone trop long (40 caractères max).'),
  logoUrl: z
    .string()
    .trim()
    .max(LOGO_MAX_OCTETS, 'Logo trop volumineux (≈ 220 Ko max). Réduis l’image ou utilise une URL.')
    .nullable()
    .optional()
    .refine(logoValide, 'Logo invalide : fournis une URL http(s) ou téléverse une image.'),
  mentionTva: texteOptionnel(200, 'Mention TVA trop longue (200 caractères max).'),
  regimeTva: z.enum(REGIMES_TVA).default('franchise'),
  conditions: texteOptionnel(1000, 'Conditions trop longues (1000 caractères max).'),
  urlAgenda: urlOptionnelle('Lien d’agenda invalide : fournis une URL commençant par http(s)://.'),
  urlCanva: urlOptionnelle('Lien Canva invalide : fournis une URL commençant par http(s)://.'),
  parametresFiscaux: parametresFiscauxSchema.nullable().optional(),
})
export type ReglagesUpsertInput = z.infer<typeof reglagesUpsertSchema>
